import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Breadcrumb, Button, message } from 'antd-v5';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import FileDownload from 'js-file-download';
import InquiryFilters from './components/InquiryFilters';
import InquiryTable from './components/InquiryTable';
import AddRolexInquiryModal from '../../../componentsV2/Rolex/AddRolexInquiryModal';
import { listInquiries, exportInquiriesCsv } from '../../../api/vendors';
import { getMerchantTags } from '../../../services/clients';
import { transformInquiry } from './utils';
import './styles.scss';

const ROLEX_VENDOR_ID = 686;
const PAGE_SIZE = 20;

const RolexInquiries = ({ auth }) => {
  const [filters, setFilters] = useState({
    clientSearch: '',
    family: '',
    status: '',
    associate: '',
    tag: '',
    dateRange: null,
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [tagLookup, setTagLookup] = useState([]);

  // Fetch merchant tags for ID → name mapping
  useEffect(() => {
    getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
      .then(response => setTagLookup(response?.results || []))
      .catch(() => {});
  }, []);

  const buildQuery = useCallback(() => {
    const query = { vendorKey: 'rolex', page, pageSize: PAGE_SIZE };
    if (filters.clientSearch) query.clientSearch = filters.clientSearch;
    if (filters.family) query.family = filters.family;
    if (filters.status) query.status = filters.status;
    if (filters.associate) query.associateId = parseInt(filters.associate, 10);
    if (filters.tag) query.tag = filters.tag;
    if (filters.dateRange?.[0]) query.startDate = filters.dateRange[0].format('YYYYMMDD');
    if (filters.dateRange?.[1]) query.endDate = filters.dateRange[1].format('YYYYMMDD');
    return query;
  }, [filters, page]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listInquiries(buildQuery());
      const stores = auth?.stores || [];
      const items = (response?.data || []).map(item => transformInquiry(item, { stores, tags: tagLookup }));
      setData(items);
      setTotal(response?.total || items.length);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, auth?.stores, tagLookup]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const query = buildQuery();
      delete query.page;
      delete query.pageSize;
      const blob = await exportInquiriesCsv(query);
      FileDownload(blob, 'rolex-inquiries.csv');
      message.success('Export complete');
    } catch {
      message.error('Export failed');
    } finally {
      setExporting(false);
    }
  }, [buildQuery]);

  const handleEdit = inquiry => {
    setEditingInquiry(inquiry);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingInquiry(null);
  };

  const handleSaved = () => {
    handleCloseModal();
    fetchData();
  };

  return (
    <div className="rolex-inquiries">
      <Breadcrumb className="rolex-inquiries__breadcrumb" items={[{ title: 'Rolex' }, { title: 'Inquiries' }]} />

      <div className="rolex-inquiries__header">
        <h1 className="rolex-inquiries__title">Inquiries</h1>
        <div className="rolex-inquiries__actions">
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
            Export
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddModal(true)}>
            Add Inquiry
          </Button>
        </div>
      </div>

      <InquiryFilters filters={filters} setFilters={setFilters} />

      <InquiryTable
        data={data}
        loading={loading}
        onEdit={handleEdit}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <AddRolexInquiryModal
        open={showAddModal || !!editingInquiry}
        onCancel={handleCloseModal}
        onSave={handleSaved}
        initialModels={editingInquiry?.models || []}
        vendorId={ROLEX_VENDOR_ID}
        inquiry={editingInquiry}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(RolexInquiries);
